import { useState } from 'react';
import type { AddressLookupOption, AddressLookupResult } from '@components/AddressCapture/AddressCaptureView';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { normalizeAddressInput } from '@utils/normalizeAddressInput';
import { classifyProxiedLookupError } from '@services/addressLookupService';
import type { AddressLookupError } from '@t/address.types';
import { useAutocompleteSession } from './useAutocompleteSession';
import { useLookupUnavailableNotice } from './useLookupUnavailableNotice';
import { usePredictionsQuery } from './usePredictionsQuery';
import { useResolutionMutation } from './useResolutionMutation';
import type { UseAddressLookupProps } from './useAddressLookup.types';

/**
 * Deliberately low, and not a lever to raise for call volume. Novus have already
 * raised suggestions not appearing quickly enough under Google, so the threshold
 * only exists to skip the first keystroke or two, where a search cannot narrow
 * anything down. Trim spend with the cache window and the debounce instead.
 */
const MIN_SEARCH_LENGTH = 3;

/**
 * A failed resolve wins over a failed search: it is the more recent thing the
 * customer did, and it is the one the view has to clear a pending selection for.
 */
const toLookupError = (resolveError: Error | null, suggestError: Error | null): AddressLookupError | undefined => {
  if (resolveError) return classifyProxiedLookupError(resolveError, 'resolve');
  if (suggestError) return classifyProxiedLookupError(suggestError, 'suggest');
  return undefined;
};

/**
 * The whole address field, whichever AddressProvider is behind the proxy. Nothing
 * here is told which one answered, and nothing here needs to be.
 *
 * Two behaviours are worth naming. A suggestion may stand for a building rather
 * than an address, and picking one re-scopes the search into it instead of
 * resolving anything — a provider with no buildings simply never sends one. And a
 * failure the proxy reports as an operator fault — a spend ceiling, an integration
 * the provider will not serve — hands the customer to manual entry, while a blip
 * is left in the field to be retried.
 */
export const useAddressLookup = ({
  debounceMs = 300,
  initialInputValue = '',
  onAddressSelected,
  onLookupUnavailable,
}: UseAddressLookupProps): AddressLookupResult => {
  const [inputValue, setInputValue] = useState(initialInputValue);
  const [frozenInput, setFrozenInput] = useState(normalizeAddressInput(initialInputValue));
  const [containerId, setContainerId] = useState<string | null>(null);
  const [selectedOption, setSelectedOption] = useState<AddressLookupOption | null>(null);

  const normalizedInput = normalizeAddressInput(inputValue);
  const debouncedInput = useDebouncedValue(normalizedInput, debounceMs);

  // Frozen against both the live and the debounced value, so the address echoed
  // back into the field on a resolve never searches for itself — not even during
  // the window before the debounce catches up.
  const isFrozen = normalizedInput === frozenInput || debouncedInput === frozenInput;

  const canSearch = debouncedInput.length >= MIN_SEARCH_LENGTH;
  const isSearching = canSearch && !isFrozen;

  const session = useAutocompleteSession();

  const predictions = usePredictionsQuery({
    searchTerm: debouncedInput,
    containerId,
    sessionToken: session.token,
    enabled: isSearching,
  });

  const resolution = useResolutionMutation({
    sessionToken: session.token,
    onResolved: (address) => {
      setInputValue(address.displayAddress);
      setFrozenInput(normalizeAddressInput(address.displayAddress));
      setContainerId(null);
      onAddressSelected(address);
    },
    onEpisodeEnded: session.rotate,
  });

  const error = toLookupError(resolution.error, predictions.error);
  // Both operator faults escalate and nothing else does. There is no credential
  // arm any more: this side holds no credential to be missing, so the first
  // response is the earliest anything can be known to be wrong.
  const cannotServeLookups = error?.kind === 'throttled' || error?.kind === 'misconfigured';
  useLookupUnavailableNotice(cannotServeLookups, onLookupUnavailable);

  const updateInputValue = (value: string): void => {
    setInputValue(value);
    // Typing is a new search, never a filter inside the building the customer
    // opened: the list goes back to offering addresses.
    setContainerId(null);
  };

  const onSelect = (option: AddressLookupOption | null): void => {
    if (!option) {
      setSelectedOption(null);
      setInputValue('');
      setFrozenInput('');
      setContainerId(null);
      return;
    }

    // Opening a building is navigation, not a choice of address, so nothing is
    // selected — which is also what keeps the building itself on offer once the
    // customer steps back out of it.
    if (option.refinesOnSelect) {
      setContainerId(option.id);
      return;
    }

    setSelectedOption(option);
    resolution.mutate(option.id);
  };

  const fetchForCurrentInput = (): void => {
    if (!isSearching) return;
    predictions.refetch();
  };

  const inFlight = predictions.isFetching || resolution.isPending;

  return {
    options: isSearching ? (predictions.data ?? []) : [],
    inputValue,
    selectedOption,
    loading: inFlight,
    busy: inFlight,
    error,
    setInputValue: updateInputValue,
    onSelect,
    fetchForCurrentInput,
    // Dismissing the list leaves the building the customer had opened, so the
    // next time they look they are offered addresses again. Anything they had
    // already resolved is untouched.
    onListClosed: () => setContainerId(null),
  };
};
